/**
 * Prints a page of its own (the card, the cellar list, the stickers, the list of allergens) from
 * the app's own window. A hidden frame does not do on the iPhone: Safari prints nothing from a frame,
 * and the installed app has no other window. So the page goes into the app's document, in a shadow
 * root (its styles and the app's stay apart); for the print only it is shown. Called right in the
 * tap, so the browser lets it print, and with the event stream to the server closed: while it is
 * open, Safari treats the page as still loading and never prints. The app on an iPhone's home
 * screen cannot print at all: there the page becomes a PDF for the share sheet (printPdf.ts).
 */
import { store } from "./store.ts";

const HOST = "print-host";
const STYLE = "print-style";

/** Takes the page of the last print away again. */
let restore: (() => void) | null = null;

/**
 * On the home screen of an iPhone or iPad iOS ignores print() without a word. There the page
 * becomes a PDF, printed from the share sheet. The browser tests set `lagerlogPrintAsPdf` to walk
 * that way too.
 */
export function printsAsPdf(): boolean {
  if ((window as { lagerlogPrintAsPdf?: boolean }).lagerlogPrintAsPdf) return true;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const standalone = (navigator as { standalone?: boolean }).standalone === true || window.matchMedia("(display-mode: standalone)").matches;
  return ios && standalone;
}

/** The PDF being made or made, or why not. Shown by `PrintSheet` in App.tsx. */
export const printPdf: { busy: boolean; file: File | null; pages: number; error: string | null } = { busy: false, file: null, pages: 0, error: null };
export const PRINT_PDF_EVENT = "lagerlog:print-pdf";
const told = () => window.dispatchEvent(new Event(PRINT_PDF_EVENT));

/** Makes the PDF; the share sheet needs a tap of its own, so `PrintSheet` offers it when ready. */
async function makePdf(html: string) {
  Object.assign(printPdf, { busy: true, file: null, pages: 0, error: null });
  told();
  try {
    const { pdfOf } = await import("./printPdf.ts");
    const { blob, pages, title } = await pdfOf(html);
    const name = `${title.replace(/[\\/:*?"<>|]+/g, " ").trim() || "Druck"}.pdf`;
    Object.assign(printPdf, { busy: false, file: new File([blob], name, { type: "application/pdf" }), pages });
  } catch (e) {
    Object.assign(printPdf, { busy: false, error: `Das PDF ließ sich nicht erstellen${e instanceof Error ? `: ${e.message}` : "."}` });
  }
  told();
}

export function closePrintPdf() {
  Object.assign(printPdf, { busy: false, file: null, pages: 0, error: null });
  told();
}

/** Hands the PDF to the share sheet (there: „Drucken“); where that is missing, saves it. */
export async function sharePrintPdf() {
  const file = printPdf.file;
  if (!file) return;
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: file.name.replace(/\.pdf$/, "") });
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) throw e;
    }
    return;
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function printPage(html: string) {
  if (printsAsPdf()) {
    void makePdf(html);
    return;
  }
  restore?.();
  restore = null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const css = [...doc.querySelectorAll("style")].map((s) => s.textContent ?? "").join("\n");
  // The page's size and margins only work in the document itself; the rest stays in the shadow root,
  // where `body` becomes the wrapper that carries the page's own body class.
  const pages = css.match(/@page[^{]*\{[^}]*\}/g) ?? [];
  const inner = css.replace(/@page[^{]*\{[^}]*\}/g, "").replace(/(^|[\s,{}(])(?:html|body)(?=[\s{.,:>)[])/g, "$1.print-body");

  const host = document.createElement("div");
  host.id = HOST;
  const root = host.attachShadow({ mode: "open" });
  root.innerHTML = `<style>${inner}</style><div class="print-body ${doc.body.className}">${doc.body.innerHTML}</div>`;
  const style = document.createElement("style");
  style.id = STYLE;
  style.textContent = `${pages.join("\n")}
@media screen { #${HOST} { display: none; } }
@media print {
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
  body > *:not(#${HOST}) { display: none !important; }
}`;
  document.head.appendChild(style);
  document.body.appendChild(host);

  // The title names the file when the print is saved as PDF.
  const title = document.title;
  if (doc.title) document.title = doc.title;
  const back = () => {
    document.title = title;
    window.removeEventListener("afterprint", back);
  };
  // The page stays (hidden on the screen) until the next print: Safari on the iPhone may report
  // the end of printing before it has laid out the page.
  restore = () => {
    back();
    host.remove();
    style.remove();
  };
  window.addEventListener("afterprint", back);
  store.pauseEventsForPrint();
  window.print();
}
